import { Stack, Typography } from '@mui/material';
import IncDrawer from 'components/drawers/inc-drawer/inc-drawer.component';
import { useDrawer } from 'components/drawers/inc-drawer/inc-drawer.hooks';
import MovementIndicator, {
  Movement,
} from 'components/movement-indicator/movement-indicator.component';
import { useTranslation } from 'react-i18next';

export type MovementLegendDrawerProps = ReturnType<typeof useDrawer>;

/**
 * Reference drawer explaining the movement badges shown on segment grids —
 * the indicator key plus the ±5% threshold rules used to classify movement.
 * Opened from the ⓘ button beside a segment grid title and from the badges
 * themselves. The example movements are FE-constructed (no BE payload), so
 * their labels come from i18n rather than the wire.
 */
const MovementLegendDrawer = (drawer: MovementLegendDrawerProps) => {
  const { t } = useTranslation();

  const MOVING_UP: Movement = {
    text: t('movementIndicator.states.movingUp'),
    direction: 'UP_RIGHT',
  };
  const MOVING_DOWN: Movement = {
    text: t('movementIndicator.states.movingDown'),
    direction: 'DOWN_LEFT',
  };
  const STABLE: Movement = {
    text: t('movementIndicator.states.stable'),
    direction: 'STABLE',
  };

  const keyEntries = [
    {
      key: 'movingUp',
      movement: MOVING_UP,
      summary: t('movementLegend.movingUp.summary'),
      detail: t('movementLegend.movingUp.detail'),
    },
    {
      key: 'movingDown',
      movement: MOVING_DOWN,
      summary: t('movementLegend.movingDown.summary'),
      detail: t('movementLegend.movingDown.detail'),
    },
    {
      key: 'stable',
      movement: STABLE,
      summary: t('movementLegend.stable.summary'),
      detail: t('movementLegend.stable.detail'),
    },
  ];

  const thresholdEntries = [
    {
      key: 'movingUp',
      movement: MOVING_UP,
      text: t('movementLegend.howCalculated.movingUp'),
    },
    {
      key: 'stable',
      movement: STABLE,
      text: t('movementLegend.howCalculated.stable'),
    },
    {
      key: 'movingDown',
      movement: MOVING_DOWN,
      text: t('movementLegend.howCalculated.movingDown'),
    },
  ];

  return (
    <IncDrawer
      {...drawer}
      title={t('movementLegend.title')}
      subtitle={t('movementLegend.subtitle')}
      data-testid="MovementLegendDrawer"
    >
      <Stack spacing={3} paddingX={3} paddingY={2}>
        <Typography variant="body2" color="text.secondary">
          {t('movementLegend.intro')}
        </Typography>

        <Stack spacing={2}>
          <Typography variant="subtitle2">
            {t('movementLegend.indicatorKey')}
          </Typography>
          {keyEntries.map(entry => (
            <Stack
              key={entry.key}
              spacing={0.5}
              data-testid={`MovementLegendDrawer.Key.${entry.key}`}
            >
              <MovementIndicator movement={entry.movement} />
              <Typography variant="body2">{entry.summary}</Typography>
              <Typography variant="caption" color="text.secondary">
                {entry.detail}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack spacing={2}>
          <Typography variant="subtitle2">
            {t('movementLegend.howCalculated.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('movementLegend.howCalculated.note')}
          </Typography>
          {thresholdEntries.map(entry => (
            <Stack
              key={entry.key}
              direction="row"
              spacing={2}
              alignItems="center"
              data-testid={`MovementLegendDrawer.Threshold.${entry.key}`}
            >
              <MovementIndicator movement={entry.movement} size="small" />
              <Typography variant="body2" color="text.secondary">
                {entry.text}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </IncDrawer>
  );
};

export default MovementLegendDrawer;
